import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ArrowRight, Bell, Megaphone, Settings } from 'lucide-react';
import { trackEvent } from 'helpers/Analytics';
import {
  isUnread,
  loadCount,
  loadPreview,
  markAllAsRead,
} from 'redux/modules/notifications';
import {
  isUnread as isAnnouncementUnread,
  loadCount as loadAnnouncementCount,
  loadPreview as loadAnnouncementPreview,
  markAllAsRead as markAllAnnouncementsAsRead,
} from 'redux/modules/announcements';
import {
  WHATS_NEW_PATH,
  entryHref,
  toNotificationContent,
} from 'components/Announcements/announcementContent';
import useOpenAnnouncement from 'components/Announcements/useOpenAnnouncement';
import NotificationItem from './NotificationItem';
import { getNotificationContent } from './notificationTypes';
import { timeAgo } from './notificationTime';
import useOpenNotification from './useOpenNotification';
import styles from './NotificationsBell.module.scss';

const COUNT_POLL_INTERVAL = 60 * 1000;

const TABS = [
  { key: 'activity', label: 'Activity' },
  { key: 'whatsnew', label: 'What’s New' },
];

export const badgeLabel = (count) => (count > 9 ? '9+' : String(count));

// Let modified clicks (new tab, new window) follow the link to /notifications
const isModifiedClick = (event) =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

/**
 * Keeps the unread counts (Activity and What's New) fresh: on route change,
 * when the window regains focus, and every minute while the tab is visible.
 */
function useUnreadCountRefresh() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      // App loads the counts on start up
      firstRender.current = false;
      return;
    }

    dispatch(loadCount());
    dispatch(loadAnnouncementCount());
  }, [dispatch, pathname]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        dispatch(loadCount());
        dispatch(loadAnnouncementCount());
      }
    };
    const interval = setInterval(refresh, COUNT_POLL_INTERVAL);

    window.addEventListener('focus', refresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
    };
  }, [dispatch]);
}

const NotificationsBell = ({ user }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { count, loadedCount, preview, previewLoaded } = useSelector(
    (state) => state.notifications
  );
  const announcements = useSelector((state) => state.announcements);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('activity');
  const wrapper = useRef(null);
  const bell = useRef(null);

  useUnreadCountRefresh();

  const openPanel = useCallback(() => {
    setOpen(true);
    dispatch(loadPreview());
    dispatch(loadAnnouncementPreview());
    trackEvent('notification-panel-opened');
  }, [dispatch]);

  const closePanel = useCallback(() => setOpen(false), []);

  // Close on a click or tap anywhere outside the bell and panel
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutside = (event) => {
      if (wrapper.current && !wrapper.current.contains(event.target)) {
        closePanel();
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open, closePanel]);

  // Close when keyboard focus moves somewhere else on the page
  const handleBlur = (event) => {
    if (
      event.relatedTarget &&
      wrapper.current &&
      !wrapper.current.contains(event.relatedTarget)
    ) {
      closePanel();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && open) {
      closePanel();

      if (bell.current) {
        bell.current.focus();
      }
    }
  };

  const handleBellClick = (event) => {
    if (isModifiedClick(event)) {
      return;
    }

    event.preventDefault();

    if (open) {
      closePanel();
    } else {
      openPanel();
    }
  };

  const openNotification = useOpenNotification('panel', closePanel);
  const openAnnouncement = useOpenAnnouncement('panel', closePanel);

  const handleMarkAll = () => {
    if (tab === 'whatsnew') {
      trackEvent('whats-new-marked-all-read', { source: 'panel' });
      dispatch(markAllAnnouncementsAsRead());
      return;
    }

    trackEvent('notifications-marked-all-read', { source: 'panel' });
    dispatch(markAllAsRead());
  };

  const goTo = (path) => (event) => {
    event.preventDefault();
    closePanel();
    history.push(path);
  };

  const activityItems = preview
    .map((notification) => ({
      notification,
      content: getNotificationContent(notification, user),
    }))
    .filter(({ content }) => content);

  const unreadActivity = loadedCount ? count : 0;
  const unreadWhatsNew = announcements.loadedCount ? announcements.count : 0;
  const unread = unreadActivity + unreadWhatsNew;
  const isActivity = tab === 'activity';
  const tabUnread = isActivity ? unreadActivity : unreadWhatsNew;

  return (
    <li
      role="presentation"
      className={`hidden-sm ${styles.wrapper}`}
      ref={wrapper}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <a
        ref={bell}
        href="/notifications"
        className={styles.bell}
        aria-label={
          unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'
        }
        aria-haspopup="true"
        aria-expanded={open}
        title="Recent notifications"
        onClick={handleBellClick}
      >
        <Bell size={22} strokeWidth={1.9} aria-hidden="true" />
        {unread > 0 && (
          <span className={styles.badge} aria-hidden="true">
            {badgeLabel(unread)}
          </span>
        )}
      </a>

      {open && (
        <div
          className={styles.panel}
          role="dialog"
          aria-label="Notifications preview"
        >
          <div className={styles.header}>
            <span className={styles.heading}>Notifications</span>
            <button
              type="button"
              className={styles.markAll}
              onClick={handleMarkAll}
              disabled={tabUnread === 0}
            >
              Mark all as read
            </button>
          </div>

          <div className={styles.tabs} role="tablist">
            {TABS.map(({ key, label }) => {
              const tabCount =
                key === 'activity' ? unreadActivity : unreadWhatsNew;

              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={`${styles.tab} ${
                    tab === key ? styles.tabSelected : ''
                  }`}
                  onClick={() => setTab(key)}
                >
                  {label}
                  {tabCount > 0 && (
                    <span className={styles.tabCount}>
                      {badgeLabel(tabCount)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {isActivity && !previewLoaded && (
            <div className={styles.loading}>Loading…</div>
          )}

          {isActivity && previewLoaded && activityItems.length === 0 && (
            <div className={styles.empty}>
              <span className={styles.emptyIcon} aria-hidden="true">
                <Bell size={22} />
              </span>
              <span className={styles.emptyTitle}>You’re all caught up</span>
              <span className={styles.emptyBody}>
                {user && user.type === 'buyer'
                  ? 'We’ll let you know here when experts apply to your jobs.'
                  : 'We’ll let you know here when a client invites you to apply.'}
              </span>
            </div>
          )}

          {isActivity && activityItems.length > 0 && (
            <ul className={styles.list}>
              {activityItems.map(({ notification, content }) => (
                <NotificationItem
                  key={notification.id}
                  id={notification.id}
                  content={content}
                  unread={isUnread(notification)}
                  time={timeAgo(notification.created_at)}
                  onOpen={openNotification(notification, content)}
                  compact
                />
              ))}
            </ul>
          )}

          {!isActivity && !announcements.previewLoaded && (
            <div className={styles.loading}>Loading…</div>
          )}

          {!isActivity &&
            announcements.previewLoaded &&
            announcements.preview.length === 0 && (
              <div className={styles.empty}>
                <span className={styles.emptyIcon} aria-hidden="true">
                  <Megaphone size={22} />
                </span>
                <span className={styles.emptyTitle}>Nothing new just yet</span>
                <span className={styles.emptyBody}>
                  We’ll let you know here when we launch or improve something.
                </span>
              </div>
            )}

          {!isActivity && announcements.preview.length > 0 && (
            <ul className={styles.list}>
              {announcements.preview.map((announcement) => (
                <NotificationItem
                  key={announcement.id}
                  id={announcement.id}
                  content={toNotificationContent(announcement)}
                  unread={isAnnouncementUnread(announcement)}
                  time={timeAgo(announcement.published_at)}
                  onOpen={openAnnouncement(
                    announcement,
                    entryHref(announcement),
                    { event: 'whats-new-entry-clicked' }
                  )}
                  compact
                  highlightIcon
                />
              ))}
            </ul>
          )}

          <div className={styles.footer}>
            {isActivity ? (
              <a
                href="/notifications"
                className={styles.footerLink}
                onClick={goTo('/notifications')}
              >
                View all notifications
                <ArrowRight size={14} aria-hidden="true" />
              </a>
            ) : (
              <a
                href={WHATS_NEW_PATH}
                className={styles.footerLink}
                onClick={goTo(WHATS_NEW_PATH)}
              >
                See what else is new
                <ArrowRight size={14} aria-hidden="true" />
              </a>
            )}
            <a
              href="/settings#notifications"
              className={styles.settings}
              aria-label="Notification settings"
            >
              <Settings size={17} aria-hidden="true" />
            </a>
          </div>
        </div>
      )}
    </li>
  );
};

NotificationsBell.propTypes = {
  user: PropTypes.object,
};

export default NotificationsBell;
